import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { clustersOf } from "../../lib/duplicate-clusters";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyDetail } from "../../lib/crm-types";
import { CompanyDialog } from "../../components/crm/CompanyDialog";
import { ContactsPanel } from "../../components/crm/ContactsPanel";
import { OwnerPicker, PicklistBadge } from "../../components/crm/FieldPickers";
import { CustomFieldsPanel } from "../../components/crm/CustomFieldsPanel";
import { DuplicateCluster } from "../../components/crm/DuplicateCluster";
import { FinancialsPanel } from "../../components/crm/FinancialsPanel";
import { FollowPanel } from "../../components/crm/FollowPanel";
import { OpportunityToggle } from "../../components/crm/OpportunityToggle";
import { GroupPanel } from "../../components/crm/GroupPanel";
import { AiSummaryPanel } from "../../components/crm/AiSummaryPanel";
import {
  ArchiveDialog,
  ArchivedBanner,
  archiveBlockedReason,
} from "../../components/crm/ArchiveCompany";
import { AtRiskPanel } from "../../components/crm/AtRiskPanel";
import { SalesCallsPanel } from "../../components/crm/SalesCallsPanel";
import { NewCallDialog } from "../../components/sales/NewCallDialog";
import { salesApi } from "../../lib/sales-api";
import { CommercialPanels } from "../../components/crm/CommercialPanels";
import { PerformancePanel } from "../../components/crm/PerformancePanel";
import { Timeline } from "../../components/crm/Timeline";
import type { ComposerMode } from "../../components/crm/ActivityComposer";
import { Button } from "../../components/ui/Button";
import { Popover } from "../../components/ui/Popover";
import { cn } from "../../components/ui/cn";
import { companiesListHref } from "../../lib/companies-back";
import { useIsDesktop } from "../../lib/media";
import type { User } from "../../types";
import { CompanyPhone } from "./CompanyPhone";

/**
 * The company page: the centre of the CRM.
 *
 * Three columns on a wide screen, one on a phone, and the order is the order
 * somebody reads them in. Left is who they are; centre is what has happened;
 * right is what the other systems know. The timeline is the widest because it
 * is the thing anybody actually came here to read.
 *
 * Below `md` it is a different page, `CompanyPhone`: one that scrolls, with
 * logging along the bottom and the rest folded away. Not the same page
 * squeezed, because what somebody wants from a company on the road is a
 * different, much shorter list -- see there.
 */
export function CompanyPage({ user }: { user: User }) {
  const { companyId } = useParams();
  const id = Number(companyId);
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  /*
   * Held here rather than inside the panel, because two things now open it --
   * the toolbar button and the panel's own -- and two copies of the dialog
   * would be two copies of its config query and two places for its state to
   * diverge.
   */
  const [raisingCall, setRaisingCall] = useState(false);
  /*
   * Held here rather than in the timeline so the header's quick actions and the
   * timeline's own composer are one control. The header is outside the
   * scrolling area, so those actions are reachable from anywhere on the page --
   * which is the point of them.
   */
  const [composeMode, setComposeMode] = useState<ComposerMode | null>(null);
  const desktop = useIsDesktop();

  const company = useQuery({
    queryKey: queryKeys.crmCompany(id),
    queryFn: () => crmApi.company(id),
    enabled: Number.isFinite(id),
    staleTime: 15_000,
  });

  /* Fetched only once somebody opens the form: the CRM has no other use for
     the sales module's config, and every company page pulling it would be a
     request per navigation for a dialog most people never open. */
  const salesConfig = useQuery({
    queryKey: queryKeys.salesConfig,
    queryFn: salesApi.config,
    enabled: raisingCall,
    staleTime: 5 * 60_000,
  });

  const lane = useQuery({
    queryKey: queryKeys.laneConnection,
    queryFn: api.laneConnection,
    staleTime: 5 * 60_000,
  });

  /*
   * Why a task cannot be raised, or null when it can.
   *
   * A reason rather than a boolean because the two ways of being unable to are
   * somebody else's problem and yours respectively, and a single "connect your
   * Lane account" message sends people to a Settings page that has no Connect
   * button on it when the server has no OAuth client at all.
   */
  const taskBlockedReason: string | null = lane.isLoading
    ? "Checking whether Lane is available…"
    : !lane.data?.available
      ? "Lane is not set up on this server. An admin needs to add the Lane OAuth client before anyone can raise tasks."
      : !lane.data.connected
        ? "Connect your own Lane account under Settings → Your settings, so tasks are raised in your name."
        : null;

  if (company.isLoading) {
    return <p className="p-8 text-center text-xs text-faint">Loading…</p>;
  }

  if (company.isError) {
    return (
      <p className="p-8 text-center text-sm text-sla-breached">
        {company.error instanceof ApiError && company.error.status === 404
          ? "That company does not exist."
          : "Could not load that company."}
      </p>
    );
  }

  const detail = company.data!;
  const archived = detail.archivedAt !== null;

  /*
   * A merged company keeps its row so old links and bookmarks resolve, and
   * sends the reader on to the survivor. Redirecting rather than showing an
   * explanation: the account they wanted still exists, it is just called
   * something else now.
   */
  if (detail.mergedIntoCompanyId) {
    return <Navigate to={`/crm/companies/${detail.mergedIntoCompanyId}`} replace />;
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {editing ? (
        <CompanyDialog company={detail} onClose={() => setEditing(false)} />
      ) : null}

      {archiving ? (
        <ArchiveDialog company={detail} onClose={() => setArchiving(false)} />
      ) : null}

      {raisingCall ? (
        <NewCallDialog
          onClose={() => setRaisingCall(false)}
          config={salesConfig.data}
          currentUserId={user.id}
          isAdmin={user.role === "admin"}
          company={{ id: detail.id, name: detail.name }}
        />
      ) : null}

      {desktop ? null : (
        <CompanyPhone
          company={detail}
          user={user}
          taskBlockedReason={taskBlockedReason}
          composeMode={composeMode}
          onComposeMode={setComposeMode}
          onEdit={() => setEditing(true)}
          onSalesCall={() => setRaisingCall(true)}
          onArchive={user.role === "admin" ? () => setArchiving(true) : null}
        />
      )}

      {desktop ? (
        <>
          <Header
            company={detail}
            onEdit={() => setEditing(true)}
            onCompose={setComposeMode}
            onSalesCall={() => setRaisingCall(true)}
            onArchive={user.role === "admin" ? () => setArchiving(true) : null}
            taskBlockedReason={taskBlockedReason}
          />

          {/* An archived company is out of the queue, so its duplicate flags are
              not a question any more; the banner says what it is instead. */}
          {detail.archivedAt ? (
            <ArchivedBanner company={detail} isAdmin={user.role === "admin"} />
          ) : (
            <DuplicateBanner company={detail} isAdmin={user.role === "admin"} />
          )}

          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto p-4 xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)_minmax(0,20rem)] xl:overflow-hidden">
            {/*
              The side columns as fieldsets, disabled on an archived company: every
              picker, toggle and Add button in them would be refused by the server,
              and a page offering them reads as though nothing happened. The
              timeline between them stays live -- its history is what somebody
              opening an archived company came to read.
            */}
            <fieldset
              disabled={archived}
              className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0 disabled:opacity-60 xl:min-h-0 xl:overflow-y-auto"
            >
              {/* A group leads with its branches: on CEF that is the record, and
                  its own details are a footnote. */}
              <GroupPanel company={detail} />
              <DetailsPanel company={detail} />
              <ContactsPanel
                companyId={detail.id}
                companyName={detail.name}
                contacts={detail.contacts}
              />
            </fieldset>

            {/*
              `xl:min-h-0`, not `min-h-0`, and the breakpoint is load bearing --
              both columns beside it already say `xl:`.

              A grid item's automatic minimum size is what stops it shrinking below
              its content. Removing it is right from `xl` up, where this column is a
              fixed-height independent scroller and `overflow-hidden` catches what
              spills. Below `xl` there is no such height and no such clip: the item
              collapsed to two pixels, `Timeline`'s `h-full` had nothing to resolve
              against, and the whole activity feed painted straight over the panel
              underneath it -- the filter chips landed on top of "What they do" and
              neither could be read.
            */}
            <div className="rounded-lg border border-line xl:min-h-0 xl:overflow-hidden">
              <Timeline
                companyId={detail.id}
                companyName={detail.name}
                contacts={detail.contacts}
                taskBlockedReason={taskBlockedReason}
                branchCount={detail.branchCount}
                composeMode={composeMode}
                onComposeMode={setComposeMode}
              />
            </div>

            <fieldset
              disabled={archived}
              className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0 disabled:opacity-60 xl:min-h-0 xl:overflow-y-auto"
            >
              <AiSummaryPanel company={detail} />
              <FollowPanel companyId={detail.id} />
              {/* Above the financials: whether anybody is chasing this account is
                  a live question, and what they have spent is a settled one. */}
              <SalesCallsPanel
                companyId={detail.id}
                onRaise={() => setRaisingCall(true)}
              />
              {/* Beside the financials, because both are about the account's
                  health rather than its details -- and this one decides whether it
                  lands on somebody's chase list in the morning. */}
              <AtRiskPanel company={detail} />
              <FinancialsPanel company={detail} />
              <CustomFieldsPanel company={detail} />
              {/* Directly above the orders it is summed from. Admins only: it shows
                  gross profit, and the route refuses anybody else as well. */}
              {user.role === "admin" ? <PerformancePanel companyId={detail.id} /> : null}
              <CommercialPanels
                company={detail}
                onCompose={setComposeMode}
              />
            </fieldset>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * "This might be the same company as that one" — said on the page, not only in
 * a queue.
 *
 * The detail response has always carried these flags and the page has never
 * shown them, so following a link out of the duplicate queue landed you on an
 * account with no sign that anything was suspected about it, and nothing to
 * press. Whoever recognises a duplicate is almost always the person who just
 * opened one of the two records; making them go and find a queue to act in is
 * how a flag stays open for a year.
 *
 * Above the three columns rather than inside one, because it is a question
 * about the whole record and not a fact about part of it.
 */
function DuplicateBanner({
  company,
  isAdmin,
}: {
  company: CompanyDetail;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();

  const open = company.duplicates.filter((flag) => flag.status === "open");
  const clusters = clustersOf(open);
  if (clusters.length === 0) return null;

  const refresh = (): void => {
    /* The whole CRM: the flag, the two companies, the queue's count in the
       sidebar, and — after a grouping — the branch lists on both sides. */
    void queryClient.invalidateQueries({ queryKey: ["crm"] });
  };

  return (
    <div className="flex flex-col gap-2 border-b border-line bg-brand-50 px-4 py-3 dark:bg-brand-950/30">
      <div>
        <p className="text-xs font-medium text-content">
          {open.length === 1
            ? "This account may already exist under another record."
            : `This account may already exist under ${open.length} other records.`}
        </p>
        {/* Said before the buttons, because the buttons include an
            irreversible one and the honest reading of a shared domain on a
            trade desk is usually "two depots", not "typed twice". */}
        <p className="text-[11px] text-muted">
          Branches of one group legitimately share a domain. Nothing is merged
          without somebody saying so.
        </p>
      </div>
      {/*
        One card per domain, the same one the queue draws.

        It used to be a grouping panel *and* a card per flag, so a company on a
        domain with three other records carried four controls saying versions
        of the same thing. The cluster card asks the one question -- branches,
        the same record, or different companies -- and answers every flag
        behind it at once.
      */}
      {clusters.map((cluster) => (
        <DuplicateCluster
          key={cluster.key}
          cluster={cluster}
          canMerge={isAdmin}
          focusCompanyId={company.id}
          onChange={refresh}
        />
      ))}
    </div>
  );
}

function Header({
  company,
  onEdit,
  onCompose,
  onSalesCall,
  onArchive,
  taskBlockedReason,
}: {
  company: CompanyDetail;
  onEdit: () => void;
  onCompose: (mode: ComposerMode) => void;
  onSalesCall: () => void;
  /** Admins only; null hides the button. */
  onArchive: (() => void) | null;
  /** Why tasks are unavailable, or null when they are not. */
  taskBlockedReason: string | null;
}) {
  const queryClient = useQueryClient();

  const filters = useQuery({
    queryKey: queryKeys.crmFilters,
    queryFn: crmApi.filters,
    staleTime: 5 * 60_000,
  });

  // Only asked when nobody owns the account, because that is the only time the
  // answer is actionable.
  const suggestion = useQuery({
    queryKey: queryKeys.crmOwnerSuggestion(company.id),
    queryFn: () => crmApi.ownerSuggestion(company.id),
    enabled: company.ownerUserId === null,
    staleTime: 60_000,
  });

  /**
   * One mutation for every field that can be changed from the header.
   *
   * They all PATCH the same company and invalidate the same two queries, and
   * the list has to be refreshed as well as the page -- a stage changed here
   * is a row that may no longer belong in the slice somebody filtered to.
   */
  const patch = useMutation({
    mutationFn: (input: Parameters<typeof crmApi.updateCompany>[1]) =>
      crmApi.updateCompany(company.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(company.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
    },
  });

  const picklists = filters.data?.picklists ?? {};
  const top = suggestion.data?.candidates[0];

  return (
    /*
     * A tinted band rather than the page's own surface. The company name is the
     * title of everything below it, and on a screen that is otherwise three
     * columns of white panels it read as the first panel rather than as the
     * heading. Grey rather than brand pink: the rail is the one thing painted
     * in the brand colour, and a second pink surface next to it competes with
     * the platform chrome instead of sitting under it.
     */
    <header className="border-b border-line bg-surface-2 px-4 py-3">
      {/* `contents`, so the fieldset adds no box -- it is only here to
          disable the pickers below on an archived company, as the side
          columns are. */}
      <fieldset disabled={company.archivedAt !== null} className="contents">
        <div className="flex flex-wrap items-center gap-2">
          {/* Back to the list as it was filtered -- see `companies-back.ts`. */}
          <Link
            to={companiesListHref()}
            className="text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
          >
            ‹ Companies
          </Link>
          {company.parent ? (
            <Link
              to={`/crm/companies/${company.parent.id}`}
              className="text-xs text-muted hover:text-brand-600"
            >
              {company.parent.name} /
            </Link>
          ) : null}

          {/* Its own row on a phone. Sharing one with the classification chips
              is what started the staircase below it: a name of unpredictable
              width followed by three chips wraps differently for every company,
              and none of the results line up. */}
          <h1 className="basis-full text-base font-semibold text-content sm:basis-auto">
            {company.name}
          </h1>

          {company.domain ? (
            <a
              href={`https://${company.domain}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted hover:text-brand-600"
            >
              {company.domain}
            </a>
          ) : null}

          {/*
            Every one of these is a dropdown in the Edit dialog, and every one of
            them is a thing somebody decides while looking at the account rather
            than while filling in a form. Shown as the badge they already were,
            and changed where they are read.

            Rendered even when unset, which they were not before: an account with
            no stage is exactly the one somebody should be able to give a stage
            to, and it was the only one with nothing to click.
          */}
          <PicklistBadge
            label="Stage"
            value={company.lifecycleStage}
            options={picklists.lifecycle_stage ?? []}
            pending={patch.isPending}
            onChange={(lifecycleStage) => patch.mutate({ lifecycleStage })}
          />
          <PicklistBadge
            label="Type"
            value={company.customerType}
            options={picklists.customer_type ?? []}
            pending={patch.isPending}
            onChange={(customerType) => patch.mutate({ customerType })}
          />
          <PicklistBadge
            label="Priority"
            value={company.leadPriority}
            options={picklists.lead_priority ?? []}
            pending={patch.isPending}
            suffix=" priority"
            onChange={(leadPriority) => patch.mutate({ leadPriority })}
          />

          {/*
            Six controls with word labels, and on a phone they are a grid rather
            than a wrapped row.

            A row that will not wrap does not clip them -- it shrinks each one
            below its content and breaks the label down the middle, so "Sales
            call" became two stacked words and "Not linked to Team" became four.
            Letting it wrap fixed that but left the next problem: six boxes of six
            different widths reflowing into a ragged staircase, which reads as a
            layout that has come apart rather than as a row of buttons. Two equal
            columns give them all the same width and the same edges, and grid
            items stretch to their cell on their own -- which is why nothing here
            asks for a width.

            From `sm` up it is the wrapped row again, `ml-auto` still pushing the
            group right of the picklists when everything fits on one line.
          */}
          {/*
            Actions only, in the order somebody works in.

            The Team link used to be the first cell of this grid. It is a link or
            a status, never an action, and as the top-left box of six identical
            ones it read as the primary button -- while "Not linked to Team",
            which is the usual case, read as a disabled one. It has moved down to
            the facts row where it belongs, and the two things people came here
            to do are now first.

            `h-9` on a phone: `size="sm"` is 28px tall, which is fine beside a
            mouse and under the 44px a thumb needs. Back to the compact size from
            `sm` up, where the row shares a line with the picklists.
          */}
          {/* None of them would be taken by an archived company; the banner
              below says so and holds the one thing that is, restoring it. */}
          {company.archivedAt ? null : (
            <div className="grid basis-full grid-cols-2 gap-2 sm:ml-auto sm:flex sm:basis-auto sm:flex-wrap sm:items-center">
              {/*
                Reachable from anywhere on the page, because the header does not
                scroll -- somebody halfway down a two-year timeline should not have
                to travel back up to record the call they have just taken.
              */}
              <Button
                size="sm"
                variant="secondary"
                className="h-9 sm:h-7"
                onClick={() => onCompose("note")}
              >
                Add note
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-9 sm:h-7"
                disabled={taskBlockedReason !== null}
                title={
                  taskBlockedReason ?? "Raise a task in Lane against this company"
                }
                onClick={() => onCompose("task")}
              >
                Add task
              </Button>
              <LogActivityMenu onCompose={onCompose} />
              {/*
                Beside the other things you do *to* an account, rather than only in
                the panel below. Somebody who has just read a company's history and
                decided it needs a ring is looking at this row, not at the third
                column.
              */}
              <Button
                size="sm"
                variant="secondary"
                className="h-9 sm:h-7"
                onClick={onSalesCall}
              >
                Sales call
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-9 sm:h-7"
                onClick={onEdit}
              >
                Edit
              </Button>
              {/* Last and quiet: the one action here that takes the company out
                  of everybody's view. Disabled with the reason rather than hidden,
                  so an admin looking for it learns why a linked company stays. */}
              {onArchive ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 sm:h-7"
                  disabled={archiveBlockedReason(company) !== null}
                  title={archiveBlockedReason(company) ?? "Take this company out of the CRM; it can be restored"}
                  onClick={onArchive}
                >
                  Archive
                </Button>
              ) : null}
            </div>
          )}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
          {company.owner ? (
            <span className="flex flex-wrap items-center gap-1 text-muted">
              Managed by
              <OwnerPicker
                ownerUserId={company.ownerUserId}
                triggerLabel={company.owner?.name ?? "Unassigned"}
                onChange={(ownerUserId) => patch.mutate({ ownerUserId })}
                pending={patch.isPending}
              />
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sla-warning">Nobody manages this account</span>
              {/*
                Directly after the sentence it answers, and the only control for
                the flag on the page -- it was a panel in the third column, and
                two places to set one thing is how somebody sets it twice and
                wonders which one counted.
              */}
              <OpportunityToggle company={company} />
              {/* The picker sits beside the suggestion rather than behind it:
                  the suggestion is one candidate with evidence, and "somebody
                  else entirely" has to stay one click away. */}
              <OwnerPicker
                ownerUserId={company.ownerUserId}
                onChange={(ownerUserId) => patch.mutate({ ownerUserId })}
                pending={patch.isPending}
                triggerLabel="Pick somebody"
              />
              {/*
                The suggestion, with its evidence and a button — never an
                automatic assignment. Who has answered the mail is strong
                evidence and not a decision.
              */}
              {top ? (
                <>
                  <span className="text-faint">
                    {top.name} — {top.ticketCount} previous ticket
                    {top.ticketCount === 1 ? "" : "s"}
                    {top.activityCount > 0
                      ? `, ${top.activityCount} activit${top.activityCount === 1 ? "y" : "ies"}`
                      : ""}
                  </span>
                  <button
                    type="button"
                    disabled={patch.isPending}
                    onClick={() => patch.mutate({ ownerUserId: top.userId })}
                    className="rounded border border-line px-1.5 py-0.5 text-[11px] text-brand-600 hover:bg-surface-2"
                  >
                    Assign
                  </button>
                </>
              ) : null}
            </span>
          )}

          {company.tradeAccountNumber ? (
            <span className="text-faint">Account {company.tradeAccountNumber}</span>
          ) : null}

          {/*
            Live from the moment a company carries a Team id, long before Team
            has an API to read from -- which is most of the value of it.
          */}
          {company.externalUrl ? (
            <a
              href={company.externalUrl}
              target="_blank"
              rel="noreferrer"
              className="text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              View in Team ↗
            </a>
          ) : (
            <span className="text-faint">Not linked to Team</span>
          )}

          {/*
            Why Add task is greyed, in words. It was a `title`, and a phone has
            no hover -- so the one button on the page that can be dead was dead
            with no way to find out why.
          */}
          {taskBlockedReason ? (
            <span className="text-faint">{taskBlockedReason}</span>
          ) : null}

          {/*
            The reserved slot for open sales calls. Deliberately empty for now:
            the behaviour has to mirror Team's, which is not specified yet, and
            inventing one would be worse than an honest gap.
          */}
        </div>

        {company.isAutoCreated ? (
          <p className="mt-2 rounded-md bg-surface-2 px-3 py-1.5 text-[11px] text-muted-on-tint">
            This company was created automatically from an email domain. Check the
            name and details, then edit it to clear this.
          </p>
        ) : null}

        {/*
          The "possibly the same as X" strip that used to sit here is now
          `DuplicateBanner`, below this header. It said who but offered nothing
          to do about it beyond a link to a queue, which is a notice rather than
          a way through -- and two notices saying the same thing would be worse
          than either.
        */}
      </fieldset>
    </header>
  );
}

/**
 * The ways contact actually happens, behind one button.
 *
 * One button rather than five, because the header is not the place to spend
 * five slots on it -- and because the choice a rep is making is "write down
 * what just happened", not "which of five buttons". A note keeps its own
 * button beside this: it is the thing written most, and every other kind is a
 * note with extra structure.
 */
function LogActivityMenu({
  onCompose,
}: {
  onCompose: (mode: ComposerMode) => void;
}) {
  const [open, setOpen] = useState(false);

  const options: { mode: ComposerMode; label: string; hint: string }[] = [
    { mode: "call", label: "Call", hint: "A phone call, in or out" },
    { mode: "whatsapp", label: "WhatsApp", hint: "A message thread" },
    {
      mode: "email",
      label: "Email",
      hint: "Correspondence that missed the helpdesk",
    },
    { mode: "meeting", label: "Meeting", hint: "In person or on a call" },
    {
      mode: "external_ticket",
      label: "Ticket",
      hint: "One handled outside Pulse",
    },
  ];

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      align="right"
      label="Log an activity against this company"
      className="w-64 p-1"
      /* Fills its grid cell on a phone the way the plain buttons beside it do.
         Explicitly `sm:w-auto`, because from `sm` up the group is a flex row
         and a full-width item there stretches to the whole header -- which is
         what it did before the breakpoint was added. */
      triggerClassName="w-full sm:w-auto"
      trigger={
        /* The same 36px as the buttons beside it on a phone, so the grid's
           rows are level and every cell is a thumb-sized target. */
        <span className="flex h-9 w-full items-center justify-center rounded-md border border-line px-2.5 text-center text-xs text-muted hover:bg-surface-2 hover:text-brand-600 sm:h-7 sm:w-auto dark:hover:text-brand-400">
          Log activity ▾
        </span>
      }
    >
      {options.map((option) => (
        <button
          key={option.mode}
          type="button"
          onClick={() => {
            onCompose(option.mode);
            setOpen(false);
          }}
          className="flex w-full flex-col gap-0.5 rounded px-2 py-1.5 text-left hover:bg-surface-2"
        >
          <span className="text-xs text-content">{option.label}</span>
          <span className="text-[11px] text-faint">{option.hint}</span>
        </button>
      ))}
    </Popover>
  );
}

function DetailsPanel({ company }: { company: CompanyDetail }) {
  const queryClient = useQueryClient();

  const setOwner = useMutation({
    mutationFn: (ownerUserId: number | null) =>
      crmApi.updateCompany(company.id, { ownerUserId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(company.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
    },
  });

  const rows: [string, React.ReactNode][] = [
    ["Trade account", company.tradeAccountNumber],
    ["Team ID", company.external?.id ?? null],
    ["Company number", company.companyNumber],
    ["VAT number", company.vatNumber],
    ["Main line", company.phone],
    ["General email", company.email],
    ["City", company.city],
    ["Region", company.region],
    /*
     * The one row here that is a choice rather than a fact somebody typed. It
     * is changeable in both places on purpose: the header is where you are
     * looking when you decide, and this panel is where you look when you are
     * asking who owns this.
     */
    [
      "Account manager",
      <OwnerPicker
        key="owner"
        ownerUserId={company.ownerUserId}
        triggerLabel={company.owner?.name ?? "Unassigned"}
        onChange={(ownerUserId) => setOwner.mutate(ownerUserId)}
        pending={setOwner.isPending}
        /*
         * Negative margins matching the trigger's own `px-1 py-0.5`, exactly.
         *
         * Popover puts its trigger inside a flex button, which turns that
         * padding from something that only paints into something that takes up
         * room: it set this one value four pixels right of every other value
         * in the panel, dropped its text two pixels below its own label, and
         * made its row taller than the eight around it. Cancelling the padding
         * here leaves it doing the job it is there for -- giving the hover
         * background room around the words -- while the row lines up with the
         * plain-text ones above and below.
         *
         * The colour comes along because `triggerClassName` replaces the
         * default rather than merging with it.
         */
        triggerClassName="-my-0.5 -ml-1 text-content"
      />,
    ],
    ["Added by", company.createdBy?.name ?? null],
  ];

  return (
    <section className="rounded-lg border border-line">
      <header className="border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          Details
        </h2>
      </header>
      <dl className="divide-y divide-line">
        {rows.map(([label, value]) => (
          <div key={label} className="flex gap-2 px-3 py-2 text-xs">
            <dt className="w-28 shrink-0 text-faint">{label}</dt>
            <dd className={cn("min-w-0 flex-1 break-words", value ? "text-content" : "text-faint")}>
              {/* Blank is a legitimate answer for a sole trader, so it is shown
                  as a dash rather than hidden -- "we did not ask" and "they do
                  not have one" look identical when the row disappears. */}
              {value ?? "—"}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

